import {useCallback} from "react"
import {restConfig} from "../authConfig"
import {useAppDispatch} from "../redux/hooks"
import {
  loading,
  userComplianceDecisionFailure,
  userComplianceDecisionSuccess,
  userComplianceDetailsFetched,
  userComplianceDetailsFetchFailure,
  userCompliancesFetched,
  userCompliancesFetchFailure,
} from "../redux/slices/userComplianceSlice"
import useFetchClient from "./useFetchClient"
import {
  ComplianceDecisionRequestModel,
  UserComplianceDetailsResponseModel,
  UserComplianceListItemModel
} from "../redux/companyMonitorTypes"
import {createUrlWithQueryString} from "../utils/url"

const useUserComplianceActions = () => {
  const fetchClient = useFetchClient()
  const dispatch = useAppDispatch()

  const toErrorMessage = useCallback((error: unknown): string => {
    if (!error) return "Unknown error"
    if (typeof error === "string") return error
    if (error instanceof Error) return error.message
    if (typeof error === "object" && "message" in error && typeof (error as any).message === "string") {
      return (error as any).message
    }
    try {
      return JSON.stringify(error)
    } catch {
      return String(error)
    }
  }, [])

  // GET list of flagged users from CompanyMonitor AML (optionally include deleted)
  const getUserCompliances = useCallback(
    (params?: { includeDeleted?: boolean }) => {
      dispatch(loading())

      // GET /backoffice/aml/users/flagged?includeDeleted=true|false
      const baseUrl = restConfig.routes.aml + "/users/flagged"
      const url = params ? createUrlWithQueryString(baseUrl, params) : baseUrl

      return fetchClient
        .get(url)
        .then((response) => {
          dispatch(userCompliancesFetched(response.data as UserComplianceListItemModel[]))
        })
        .catch((error) => {
          const errorMessage = toErrorMessage(error)
          console.log("getUserCompliances error:", errorMessage)
          dispatch(userCompliancesFetchFailure(errorMessage))
        })
    },
    [dispatch, fetchClient, toErrorMessage],
  )

  // GET user compliance details by user id
  const getUserComplianceDetails = useCallback(
    (userId: string) => {
      dispatch(loading())

      // swagger: GET /backoffice/aml/users/{id}
      const url = restConfig.routes.aml + `/users/${userId}`

      return fetchClient
        .get(url)
        .then((response) => {
          dispatch(userComplianceDetailsFetched(response.data as UserComplianceDetailsResponseModel))
        })
        .catch((error) => {
          const errorMessage = toErrorMessage(error)
          console.log("getUserComplianceDetails error:", errorMessage)
          dispatch(userComplianceDetailsFetchFailure(errorMessage))
        })
    },
    [dispatch, fetchClient, toErrorMessage],
  )

  // POST compliance decision for a specific report for a user
  const postUserComplianceDecision = useCallback(
    (userId: string, reportId: string, decision: ComplianceDecisionRequestModel) => {
      dispatch(loading())

      // swagger: POST /backoffice/aml/users/{id}/reports/{reportId}/decisions
      const url = restConfig.routes.aml + `/users/${userId}/reports/${reportId}/decisions`

      return fetchClient
        .post(url, decision)
        .then((response) => {
          dispatch(userComplianceDecisionSuccess(response.data as UserComplianceDetailsResponseModel))
        })
        .catch((error) => {
          const errorMessage = toErrorMessage(error)
          console.log("postUserComplianceDecision error:", errorMessage)
          dispatch(userComplianceDecisionFailure(errorMessage))
        })
    },
    [dispatch, fetchClient, toErrorMessage],
  )

  return {
    getUserCompliances,
    getUserComplianceDetails,
    postUserComplianceDecision,
  }
}

export default useUserComplianceActions
